/**
 * V2 admin — Marketing tab: the creator-outreach pipeline.
 *
 * The play is docs/CREATOR-EMBED-CAMPAIGN.md; this is the surface that runs
 * it. It opens with the only two questions the campaign actually asks each
 * day — who is due a follow-up, and what is stuck — because a pipeline you
 * have to go looking through is a pipeline that goes quiet.
 *
 * Everything here is admin-only by RLS (20260814_marketing_prospects.sql).
 * If that migration has not been run, the tab says so by name on-screen
 * rather than failing blank: the founder tests on an iPad with no DevTools,
 * so "why is this empty" has to be answerable from the page itself.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { fetchAll } from './data.js';

/** The funnel, in order. Must match the status CHECK in the migration. */
export const STAGES = [
  { id: 'prospect', label: 'Prospect', hint: 'Found, not worked yet' },
  { id: 'list_built', label: 'List built', hint: 'Prebuilt list ready, unlisted' },
  { id: 'pitched', label: 'Pitched', hint: 'Note sent' },
  { id: 'replied', label: 'Replied', hint: 'They wrote back' },
  { id: 'claimed', label: 'Claimed', hint: 'List is theirs' },
  { id: 'embedded', label: 'Embedded', hint: 'Live on their site' },
  { id: 'featured', label: 'Featured', hint: 'On our category page too' },
  { id: 'declined', label: 'Declined', hint: 'No is no' },
  { id: 'no_response', label: 'No response', hint: 'Follow-up spent' },
];

const STAGE_LABEL = new Map(STAGES.map((s) => [s.id, s.label]));

/** Stages that still want something from us — the ones "due" logic applies to. */
const OPEN_STAGES = new Set(['prospect', 'list_built', 'pitched', 'replied', 'claimed']);

const PLATFORMS = ['Blog', 'YouTube', 'TikTok', 'Instagram', 'Substack', 'Pinterest',
  'Facebook Group', 'Reddit', 'Other'];
const EXISTING_MAP = ['None', 'Google My Maps', 'Other embed'];

/** Local (not UTC) YYYY-MM-DD — a date field must mean the day the founder is having. */
export function todayISO(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * One count per stage, plus the two numbers the campaign is judged on:
 * live embeds (Embedded + Featured — the only number that compounds) and
 * the claim rate over everyone actually pitched.
 */
export function funnelCounts(rows) {
  const byStage = Object.fromEntries(STAGES.map((s) => [s.id, 0]));
  for (const row of rows) {
    if (byStage[row.status] !== undefined) byStage[row.status] += 1;
  }
  const liveEmbeds = byStage.embedded + byStage.featured;
  const claimed = byStage.claimed + liveEmbeds;
  // Everyone who has been pitched, whatever happened next
  const pitchedEver = byStage.pitched + byStage.replied + claimed
    + byStage.declined + byStage.no_response;
  return {
    byStage,
    total: rows.length,
    liveEmbeds,
    pitchedEver,
    claimRate: pitchedEver ? Math.round((claimed / pitchedEver) * 100) : null,
  };
}

/**
 * What needs the founder today: follow-ups due or overdue, still-open only.
 * A date in the past is not a missed chore to hide — it is the top of the
 * list, so overdue sorts first.
 */
export function dueFollowUps(rows, today = todayISO()) {
  return rows
    .filter((r) => OPEN_STAGES.has(r.status) && r.next_followup && r.next_followup <= today)
    .sort((a, b) => (a.next_followup < b.next_followup ? -1 : a.next_followup > b.next_followup ? 1 : 0));
}

/** Search + status/tier filter. Exported for tests. */
export function filterProspects(rows, { search = '', status = '', tier = '' } = {}) {
  let result = [...rows];
  if (status) result = result.filter((r) => r.status === status);
  if (tier) result = result.filter((r) => String(r.tier || '') === String(tier));
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((r) => [r.creator, r.category, r.content_url, r.notes, r.platform]
      .some((v) => (v || '').toLowerCase().includes(term)));
  }
  return result.sort((a, b) => (b.found_on || '').localeCompare(a.found_on || ''));
}

/**
 * The UTM slug for a creator: their domain, stripped to something that reads
 * in a report ("leonettiliving"). Attribution stamps this on every event
 * their readers ever generate, so it has to be stable and human-readable.
 */
export function utmSlug(url, creator = '') {
  let host = '';
  try {
    host = new URL(url).hostname.replace(/^www\./, '');
  } catch { /* not a URL — fall back to the name */ }
  const source = host
    ? host.replace(/\.(com|net|org|co|io|blog|xyz)(\.[a-z]{2})?$/i, '')
    : creator;
  return source.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

/** The tracked link for a prospect's pitch — the convention, in one place. */
export function utmLink(listUrl, slug) {
  if (!listUrl || !slug) return '';
  const sep = listUrl.includes('?') ? '&' : '?';
  return `${listUrl}${sep}utm_source=${encodeURIComponent(slug)}&utm_medium=outreach&utm_campaign=creators`;
}

const FIELDS = [
  ['creator', 'Creator', 'text', true],
  ['content_url', 'Their post / channel URL', 'url', false],
  ['platform', 'Platform', 'select', false],
  ['category', 'Category', 'text', false],
  ['tier', 'Tier', 'tier', false],
  ['audience', 'Audience size', 'text', false],
  ['existing_map', 'Existing map', 'map', false],
  ['contact_channel', 'Contact channel', 'text', false],
  ['prebuilt_list_url', 'Prebuilt list URL', 'url', false],
  ['next_followup', 'Next follow-up', 'date', false],
  ['notes', 'Notes', 'textarea', false],
];

export function mount(el) {
  let destroyed = false;
  let all = [];
  let loaded = false;
  let loadError = '';
  let tableMissing = false;
  let search = '';
  let statusFilter = '';
  let tierFilter = '';
  let editing = null; // prospect id being edited, or 'new'

  async function load() {
    all = await fetchAll(gateway.TABLES.MARKETING_PROSPECTS, { order: 'found_on.desc' });
    loaded = true;
  }

  function fieldHtml([key, label, type], row) {
    const value = row?.[key] ?? '';
    const id = `mk-${key}`;
    let input;
    if (type === 'select') {
      input = `<select id="${id}" name="${key}">
        <option value="">—</option>
        ${PLATFORMS.map((p) => `<option ${value === p ? 'selected' : ''}>${p}</option>`).join('')}
      </select>`;
    } else if (type === 'map') {
      input = `<select id="${id}" name="${key}">
        <option value="">—</option>
        ${EXISTING_MAP.map((p) => `<option ${value === p ? 'selected' : ''}>${p}</option>`).join('')}
      </select>`;
    } else if (type === 'tier') {
      input = `<select id="${id}" name="${key}">
        <option value="">—</option>
        ${[1, 2, 3].map((t) => `<option value="${t}" ${String(value) === String(t) ? 'selected' : ''}>Tier ${t}</option>`).join('')}
      </select>`;
    } else if (type === 'date') {
      // iOS gives a date input no way back to empty once it holds a value —
      // and clearing the follow-up is the move the playbook's one-nudge rule
      // depends on, so the field carries its own way out (2026-08-25, found
      // on the iPad with the first follow-up actually due).
      input = `<span class="marketing-date-field">
        <input id="${id}" name="${key}" type="date" value="${escapeHtml(String(value))}">
        <button type="button" class="admin-btn admin-btn-xs" data-clear-date="${id}">Clear</button>
      </span>`;
    } else if (type === 'textarea') {
      input = `<textarea id="${id}" name="${key}" rows="2">${escapeHtml(String(value))}</textarea>`;
    } else {
      input = `<input id="${id}" name="${key}" type="${type}" value="${escapeHtml(String(value))}"
        autocomplete="off">`;
    }
    return `<label class="v2-field">${label}${input}</label>`;
  }

  function formHtml(row) {
    const isNew = !row;
    return `
      <form class="admin-card admin-card-span marketing-form" data-marketing-form
        ${isNew ? '' : `data-editing="${row.id}"`}>
        <h3 class="admin-card-title">${isNew ? 'Log a prospect' : `Edit ${escapeHtml(row.creator)}`}</h3>
        <div class="marketing-fields">
          ${FIELDS.map((f) => fieldHtml(f, row)).join('')}
          <label class="v2-field">Status
            <select name="status">
              ${STAGES.map((s) => `<option value="${s.id}" ${(row?.status || 'prospect') === s.id ? 'selected' : ''}>${s.label}</option>`).join('')}
            </select>
          </label>
        </div>
        <div class="admin-actions">
          <button type="submit" class="admin-btn admin-btn-primary">${isNew ? 'Add to pipeline' : 'Save'}</button>
          <button type="button" class="admin-btn" data-marketing-cancel>Cancel</button>
        </div>
      </form>`;
  }

  function dueHtml(due) {
    if (!due.length) {
      return `<div class="admin-card admin-card-span marketing-due marketing-due-clear">
        <h3 class="admin-card-title">✅ Nothing due today</h3>
        <p class="admin-muted">Next move: run a query recipe for a Tier 1 category and log 10 prospects.</p>
      </div>`;
    }
    const today = todayISO();
    return `
      <div class="admin-card admin-card-span marketing-due">
        <h3 class="admin-card-title">🔔 Needs you today (${due.length})</h3>
        <ul class="marketing-due-list">
          ${due.map((r) => `
            <li>
              <strong>${escapeHtml(r.creator)}</strong>
              <span class="admin-muted">${escapeHtml(STAGE_LABEL.get(r.status) || r.status)}</span>
              <span class="${r.next_followup < today ? 'marketing-overdue' : 'admin-muted'}">
                ${r.next_followup < today ? `overdue since ${escapeHtml(r.next_followup)}` : 'due today'}</span>
              <button type="button" class="admin-btn admin-btn-xs" data-followed-up="${r.id}">Followed up</button>
              <button type="button" class="admin-btn admin-btn-xs" data-edit-prospect="${r.id}">Open</button>
            </li>`).join('')}
        </ul>
      </div>`;
  }

  function funnelHtml(counts) {
    return `
      <div class="admin-card admin-card-span marketing-funnel">
        <h3 class="admin-card-title">Pipeline</h3>
        <div class="marketing-stages">
          ${STAGES.map((s) => `
            <button type="button" class="marketing-stage ${statusFilter === s.id ? 'is-active' : ''}"
              data-stage-filter="${s.id}" title="${s.hint}">
              <span class="marketing-stage-count">${counts.byStage[s.id]}</span>
              <span class="marketing-stage-label">${s.label}</span>
            </button>`).join('')}
        </div>
        <div class="admin-kpis marketing-kpis">
          <div class="admin-kpi admin-kpi-green">
            <span class="admin-kpi-value">${counts.liveEmbeds}</span>
            <span class="admin-kpi-label">Live embeds</span>
            <span class="admin-kpi-sub">The number that compounds</span>
          </div>
          <div class="admin-kpi">
            <span class="admin-kpi-value">${counts.claimRate === null ? '—' : `${counts.claimRate}%`}</span>
            <span class="admin-kpi-label">Claim rate</span>
            <span class="admin-kpi-sub">${counts.pitchedEver} pitched${
              counts.claimRate !== null && counts.claimRate < 10 ? ' · under 10%: better lists, not more pitches' : ''}</span>
          </div>
          <div class="admin-kpi">
            <span class="admin-kpi-value">${counts.total}</span>
            <span class="admin-kpi-label">In pipeline</span>
          </div>
        </div>
      </div>`;
  }

  function rowHtml(r) {
    const slug = r.utm_source || utmSlug(r.content_url || '', r.creator || '');
    const link = utmLink(r.prebuilt_list_url, slug);
    return `
      <tr>
        <td class="admin-cell-clamp">
          <strong>${escapeHtml(r.creator)}</strong>
          ${r.content_url ? `<br><a class="admin-link" href="${escapeHtml(r.content_url)}"
            target="_blank" rel="noopener">${escapeHtml(r.platform || 'post')} ↗</a>` : ''}
        </td>
        <td class="admin-cell-clamp">${escapeHtml(r.category || '—')}</td>
        <td>${r.tier ? `T${r.tier}` : '—'}</td>
        <td>
          <select class="marketing-status-select" data-status-for="${r.id}" aria-label="Status for ${escapeHtml(r.creator)}">
            ${STAGES.map((s) => `<option value="${s.id}" ${r.status === s.id ? 'selected' : ''}>${s.label}</option>`).join('')}
          </select>
        </td>
        <td class="admin-cell-dim">${escapeHtml(r.next_followup || '—')}</td>
        <td class="admin-cell-clamp admin-cell-dim">
          ${link ? `<button type="button" class="admin-link" data-copy-utm="${escapeHtml(link)}">Copy tracked link</button>`
            : '<span class="admin-cell-dim">Add a list URL</span>'}
        </td>
        <td>
          <div class="admin-actions">
            <button type="button" class="admin-btn admin-btn-xs" data-edit-prospect="${r.id}">Edit</button>
            <button type="button" class="admin-btn admin-btn-xs admin-btn-danger" data-delete-prospect="${r.id}">Delete</button>
          </div>
        </td>
      </tr>`;
  }

  function render() {
    if (tableMissing) {
      el.innerHTML = `
        <div class="admin-card admin-card-span">
          <h3 class="admin-card-title">One migration to run first</h3>
          <p>The pipeline table isn’t in the database yet. Run
            <code>supabase/migrations/20260814_marketing_prospects.sql</code>
            in the Supabase SQL editor, then tap Refresh above.</p>
          <p class="admin-muted">Everything else in the panel works meanwhile — this tab is the only thing waiting on it.</p>
        </div>`;
      return;
    }
    if (loadError) {
      el.innerHTML = `<div class="v2-error">${escapeHtml(loadError)}</div>`;
      return;
    }
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Opening the pipeline…</div>';
      return;
    }

    const counts = funnelCounts(all);
    const due = dueFollowUps(all);
    const shown = filterProspects(all, { search, status: statusFilter, tier: tierFilter });
    const editRow = editing && editing !== 'new' ? all.find((r) => r.id === editing) : null;

    el.innerHTML = `
      ${dueHtml(due)}
      ${funnelHtml(counts)}
      ${editing ? formHtml(editRow) : ''}
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-marketing-search aria-label="Search prospects"
            placeholder="Search creators, categories, notes…" value="${escapeHtml(search)}" autocomplete="off">
          <select class="admin-btn" data-tier-filter aria-label="Filter by tier">
            <option value="">All tiers</option>
            ${[1, 2, 3].map((t) => `<option value="${t}" ${tierFilter === String(t) ? 'selected' : ''}>Tier ${t}</option>`).join('')}
          </select>
          ${statusFilter ? `<button type="button" class="admin-btn" data-clear-stage>Clear “${
            escapeHtml(STAGE_LABEL.get(statusFilter) || statusFilter)}” ✕</button>` : ''}
          <span class="admin-muted">${shown.length} of ${all.length}</span>
          <span class="admin-toolbar-spacer"></span>
          ${editing ? '' : '<button type="button" class="admin-btn admin-btn-primary" data-new-prospect>+ Log a prospect</button>'}
        </div>
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr><th>Creator</th><th>Category</th><th>Tier</th><th>Status</th>
                <th>Follow-up</th><th>Tracked link</th><th>Actions</th></tr>
            </thead>
            <tbody>
              ${shown.length ? shown.map(rowHtml).join('')
                : `<tr><td colspan="7" class="admin-empty-cell">${all.length
                  ? 'Nothing matches those filters'
                  : 'No prospects yet — “+ Log a prospect” starts the pipeline.'}</td></tr>`}
            </tbody>
          </table>
        </div>
        <p class="admin-muted">The play, the query recipes and the templates live in
          <code>docs/CREATOR-EMBED-CAMPAIGN.md</code>. Rule of the house: never pitch without a prebuilt
          list, and one follow-up only.</p>
      </div>`;

    el.querySelector('[data-marketing-search]')?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      render();
      const again = el.querySelector('[data-marketing-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));
  }

  async function save(form) {
    const data = new FormData(form);
    const body = {};
    for (const [key] of [...FIELDS.map((f) => [f[0]]), ['status']]) {
      const value = String(data.get(key) ?? '').trim();
      body[key] = value === '' ? null : value;
    }
    if (!body.creator) return showToast('A creator name is the one required field.', { type: 'info' });
    body.tier = body.tier ? Number(body.tier) : null;
    body.status = body.status || 'prospect';
    if (!body.utm_source) body.utm_source = utmSlug(body.content_url || '', body.creator) || null;

    const id = form.dataset.editing;
    if (id) {
      body.updated_at = new Date().toISOString();
      const { error } = await gateway.rawUpdate(gateway.TABLES.MARKETING_PROSPECTS, {
        filters: { id: `eq.${id}` }, body,
      });
      if (destroyed) return;
      if (error) return showToast('Couldn’t save that prospect.', { type: 'info' });
      all = all.map((r) => (r.id === id ? { ...r, ...body } : r));
      showToast('Saved.', { type: 'success' });
    } else {
      body.found_on = body.found_on || todayISO();
      const { data: inserted, error } = await gateway.rawInsert(gateway.TABLES.MARKETING_PROSPECTS, { body });
      if (destroyed) return;
      if (error) return showToast('Couldn’t add that prospect.', { type: 'info' });
      all = [...(inserted || []), ...all];
      showToast('Logged. Build their list before you pitch.', { type: 'success' });
    }
    editing = null;
    render();
  }

  async function setStatus(id, status) {
    const row = all.find((r) => r.id === id);
    if (!row || row.status === status) return;
    const previous = row.status;
    row.status = status; // optimistic: the select already shows it
    const { error } = await gateway.rawUpdate(gateway.TABLES.MARKETING_PROSPECTS, {
      filters: { id: `eq.${id}` },
      body: { status, updated_at: new Date().toISOString() },
    });
    if (destroyed) return;
    if (error) {
      row.status = previous;
      showToast('Couldn’t move that one.', { type: 'info' });
    } else if (status === 'pitched' && !row.next_followup) {
      // The one follow-up the playbook allows, dated so it can nag: ~10 days.
      const due = new Date();
      due.setDate(due.getDate() + 10);
      const next = todayISO(due);
      const { error: dueError } = await gateway.rawUpdate(gateway.TABLES.MARKETING_PROSPECTS, {
        filters: { id: `eq.${id}` }, body: { next_followup: next },
      });
      if (!dueError) row.next_followup = next;
      showToast(`Pitched — follow-up set for ${next}.`, { type: 'success' });
    }
    render();
  }

  /**
   * "I sent the nudge." Clears the date so the row stops nagging, and leaves
   * the status alone — the playbook spends the ONE follow-up here; whether it
   * turns into Replied or No response is a later, separate call.
   */
  async function clearFollowUp(id) {
    const row = all.find((r) => r.id === id);
    if (!row || !row.next_followup) return;
    const previous = row.next_followup;
    row.next_followup = null;
    render();
    const { error } = await gateway.rawUpdate(gateway.TABLES.MARKETING_PROSPECTS, {
      filters: { id: `eq.${id}` },
      body: { next_followup: null, updated_at: new Date().toISOString() },
    });
    if (destroyed) return;
    if (error) {
      row.next_followup = previous;
      showToast('Couldn\u2019t clear that follow-up.', { type: 'info' });
      return render();
    }
    showToast('Follow-up cleared \u2014 that was the one.', { type: 'success' });
  }

  async function remove(id) {
    const row = all.find((r) => r.id === id);
    if (!row) return;
    if (!(await confirmDialog({
      title: `Remove ${row.creator}?`,
      message: 'This only clears the pipeline row — any list you built for them stays put.',
    }))) return;
    const { error } = await gateway.rawDelete(gateway.TABLES.MARKETING_PROSPECTS, {
      filters: { id: `eq.${id}` },
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t remove that row.', { type: 'info' });
    all = all.filter((r) => r.id !== id);
    render();
  }

  async function copyUtm(link, button) {
    try {
      await navigator.clipboard.writeText(link);
      showToast('Tracked link copied — paste it into the pitch.', { type: 'success' });
    } catch {
      // No clipboard (older WebView): show it so it can be copied by hand
      if (button) button.outerHTML = `<code class="marketing-utm">${escapeHtml(link)}</code>`;
    }
  }

  function onClick(event) {
    const t = event.target;
    const stage = t.closest('[data-stage-filter]');
    if (stage) {
      const id = stage.dataset.stageFilter;
      statusFilter = statusFilter === id ? '' : id;
      return render();
    }
    if (t.closest('[data-clear-stage]')) { statusFilter = ''; return render(); }
    const followed = t.closest('[data-followed-up]');
    if (followed) return clearFollowUp(followed.dataset.followedUp);
    const clearDate = t.closest('[data-clear-date]');
    if (clearDate) {
      const input = el.querySelector(`#${clearDate.dataset.clearDate}`);
      if (input) input.value = '';
      return;
    }
    if (t.closest('[data-new-prospect]')) { editing = 'new'; return render(); }
    if (t.closest('[data-marketing-cancel]')) { editing = null; return render(); }
    const edit = t.closest('[data-edit-prospect]');
    if (edit) { editing = edit.dataset.editProspect; return render(); }
    const del = t.closest('[data-delete-prospect]');
    if (del) return remove(del.dataset.deleteProspect);
    const copy = t.closest('[data-copy-utm]');
    if (copy) return copyUtm(copy.dataset.copyUtm, copy);
  }

  function onChange(event) {
    const status = event.target.closest('[data-status-for]');
    if (status) return setStatus(status.dataset.statusFor, status.value);
    const tier = event.target.closest('[data-tier-filter]');
    if (tier) { tierFilter = tier.value; return render(); }
  }

  function onSubmit(event) {
    const form = event.target.closest('[data-marketing-form]');
    if (!form) return;
    event.preventDefault();
    save(form);
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch((error) => {
      if (destroyed) return;
      // PostgREST answers a missing table with 404 and an undefined-table code
      const message = String(error?.message || '');
      if (message.includes('404') || message.includes('PGRST205') || message.includes('42P01')) {
        tableMissing = true;
      } else {
        loadError = 'Couldn’t load the pipeline. If you’re signed in as an admin and this persists, '
          + 'check that 20260814_marketing_prospects.sql ran.';
      }
      render();
    });

  el.addEventListener('click', onClick);
  el.addEventListener('change', onChange);
  el.addEventListener('submit', onSubmit);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
    el.removeEventListener('change', onChange);
    el.removeEventListener('submit', onSubmit);
  };
}
